import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DoqitLogo } from 'common/designSystem/iconComponents';
import { linkPath } from 'common/constants';
import { HomeIcon, BellIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { HomeIcon as HomeIconSolid, BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import { useAppDispatch, useAppSelector } from 'common/hooks/state';
import { resetUploadData, seedDefaultReminderLeadTime } from 'common/store/appSlice';
import { RootState } from 'common/store/store';
import UserDropdownMenu from 'common/components/UserDropdownMenu';
import { applyUploadPreset, getUploadPreset } from 'features/UploadDocument/uploadPreset';

interface DesktopHeaderProps {
  className?: string;
}

export const DesktopHeader: React.FC<DesktopHeaderProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const [searchQuery, setSearchQuery] = useState('');

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleAddDocument = () => {
    dispatch(resetUploadData());
    // Seed the fresh draft with the user's saved default reminder lead time
    // (Notifications settings). Null/unset profile values leave the
    // reducer's own static default in place.
    const defaultBefore = user?.user?.default_reminder_before ?? user?.default_reminder_before ?? null;
    const defaultBeforeType = user?.user?.default_reminder_before_type ?? user?.default_reminder_before_type ?? null;
    if (defaultBefore != null && defaultBeforeType) {
      dispatch(seedDefaultReminderLeadTime({ before: defaultBefore, beforeType: defaultBeforeType }));
    }
    // On a category or group page, keep that context (see uploadPreset.ts).
    applyUploadPreset(dispatch, getUploadPreset());
    navigate(linkPath.documentUpload);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Navigate to search page with query - search page will handle the search
    navigate(linkPath.searchDocumentPath, { state: { query: searchQuery.trim() } });
    setSearchQuery(''); // Clear the header input after navigating
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-white shadow-desktop-header ${className}`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="max-w-desktop-xl mx-auto px-6 h-desktop-header flex items-center justify-between gap-4">
        {/* Left Section: Logo + Nav Icons */}
        <div className="flex items-center gap-6">
          {/* Logo */}
          <button
            type="button"
            onClick={() => navigate(linkPath.dashboard)}
            className="flex-shrink-0"
          >
            <DoqitLogo className="w-[80px] h-[45px]" />
          </button>

          {/* Navigation Icons */}
          <nav className="flex items-center gap-1">
            {/* Home */}
            <button
              type="button"
              onClick={() => navigate(linkPath.dashboard)}
              className={`p-2.5 rounded-xl transition-colors ${
                isActive(linkPath.dashboard)
                  ? 'bg-doqit-primary text-doqit-button-primary'
                  : 'text-doqit-purple hover:bg-gray-100'
              }`}
              title="Home"
            >
              {isActive(linkPath.dashboard) ? (
                <HomeIconSolid className="w-6 h-6" />
              ) : (
                <HomeIcon className="w-6 h-6" />
              )}
            </button>

            {/* Notifications/Reminders */}
            <button
              type="button"
              onClick={() => navigate(linkPath.reminder)}
              className={`p-2.5 rounded-xl transition-colors ${
                isActive(linkPath.reminder)
                  ? 'bg-doqit-primary text-doqit-button-primary'
                  : 'text-doqit-purple hover:bg-gray-100'
              }`}
              title="Reminders"
              data-tour="nav-reminders"
            >
              {isActive(linkPath.reminder) ? (
                <BellIconSolid className="w-6 h-6" />
              ) : (
                <BellIcon className="w-6 h-6" />
              )}
            </button>
          </nav>

          {/* Add Button */}
          <button
            type="button"
            onClick={handleAddDocument}
            className="transition-all hover:opacity-90 hover:scale-105"
            data-tour="add-doc"
          >
            <img
              src="/images/Add Nav Icon.svg"
              alt="Add"
              className="h-[40px] w-auto"
            />
          </button>
        </div>

        {/* Center Section: Search Bar */}
        <div className="flex-1 max-w-xl">
          <form onSubmit={handleSearch} className="relative" data-tour="search-pill">
            <input
              type="text"
              placeholder="Search your documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full px-5 py-3 pl-5 pr-12 bg-gray-50 border border-gray-200 rounded-full
                text-doqit-purple placeholder-doqit-purple-light focus:outline-none focus:ring-2
                focus:ring-doqit-button-primary focus:border-transparent transition-all`}
            />
            <button
              type="submit"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-doqit-button-primary hover:text-doqit-purple transition-colors"
            >
              <MagnifyingGlassIcon className="w-5 h-5" />
            </button>
          </form>
        </div>

        {/* Right Section: Help + User */}
        <div className="flex items-center gap-4">
          {/* Help Link */}
          <button
            type="button"
            onClick={() => window.open('https://www.doqit.io/faqs', '_blank')}
            className="text-doqit-purple font-medium hover:text-doqit-button-primary transition-colors"
          >
            Help
          </button>

          {/* User Dropdown */}
          <UserDropdownMenu />
        </div>
      </div>
    </header>
  );
};

export default DesktopHeader;
